/**
 * ConversionTaskMonitor Component
 * 
 * Example component showing how to use the task-based conversion API with polling.
 * This can be integrated into your existing ChatInterface or used standalone.
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

const POLL_INTERVAL_MS = 5000;

const ConversionTaskMonitor = ({ taskId, apiMethod, onComplete, onError }) => {
  const [taskStatus, setTaskStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef(null);
  const getTaskStatusRef = useRef(apiMethod || api.v2.getTaskStatus);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Use provided API method or default to v2.getTaskStatus
  const getTaskStatus = apiMethod || api.v2.getTaskStatus;

  useEffect(() => {
    getTaskStatusRef.current = getTaskStatus;
  }, [getTaskStatus]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Poll frequently so completion is reflected quickly in the chat UI.
  useEffect(() => {
    if (!taskId) return;

    const pollTaskStatus = async () => {
      try {
        const status = await getTaskStatusRef.current(taskId);
        setTaskStatus(status);

        // Stop polling if task is completed or failed
        // Check both status and progress_percentage for completion
        const isCompleted = status.status === 'COMPLETED' || 
                           (status.status === 'IN_PROGRESS' && status.progress_percentage >= 100);
        
        if (isCompleted) {
          stopPolling();
          if (onCompleteRef.current) {
            onCompleteRef.current(status);
          }
        } else if (status.status === 'FAILED') {
          stopPolling();
          if (onErrorRef.current) {
            onErrorRef.current(status.error_message || 'Conversion failed');
          }
        }
      } catch (error) {
        console.error('Error polling task status:', error);
        // Don't stop polling on network errors, keep trying
      }
    };

    const startPolling = () => {
      setIsPolling(true);
      // Poll immediately
      pollTaskStatus();
      // Then poll every 5 seconds
      pollingIntervalRef.current = setInterval(pollTaskStatus, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      setIsPolling(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    startPolling();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [taskId]);

  if (!taskStatus) {
    return (
      <div className="conversion-task-monitor">
        <div className="loading-spinner"></div>
        <p>Initializing conversion task...</p>
      </div>
    );
  }

  const getStatusIcon = () => {
    // Consider 100% progress as completed even if status hasn't updated yet
    const isCompleted = taskStatus.status === 'COMPLETED' || 
                       (taskStatus.status === 'IN_PROGRESS' && taskStatus.progress_percentage >= 100);
    
    if (isCompleted) {
      return '✅';
    }
    
    switch (taskStatus.status) {
      case 'PENDING':
        return '⏳';
      case 'IN_PROGRESS':
        return '🔄';
      case 'FAILED':
        return '❌';
      default:
        return '❓';
    }
  };

  const getStatusColor = () => {
    // Consider 100% progress as completed even if status hasn't updated yet
    const isCompleted = taskStatus.status === 'COMPLETED' || 
                       (taskStatus.status === 'IN_PROGRESS' && taskStatus.progress_percentage >= 100);
    
    if (isCompleted) {
      return '#4CAF50';
    }
    
    switch (taskStatus.status) {
      case 'PENDING':
        return '#FFA500';
      case 'IN_PROGRESS':
        return '#2196F3';
      case 'FAILED':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  return (
    <div className="conversion-task-monitor" style={{ 
      padding: '15px', 
      border: `2px solid ${getStatusColor()}`,
      borderRadius: '8px',
      margin: '10px 0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '24px' }}>{getStatusIcon()}</span>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 5px 0' }}>
            Converting: {taskStatus.form_name}
          </h4>
          <p style={{ margin: '0', color: '#666' }}>
            Status: <strong>
              {taskStatus.status === 'IN_PROGRESS' && taskStatus.progress_percentage >= 100 
                ? 'COMPLETED' 
                : taskStatus.status}
            </strong>
            {taskStatus.current_phase && ` - ${taskStatus.current_phase}`}
          </p>
          {taskStatus.progress_percentage !== undefined && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ 
                width: '100%', 
                height: '6px', 
                background: '#e0e0e0',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${taskStatus.progress_percentage}%`,
                  height: '100%',
                  background: getStatusColor(),
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                {taskStatus.progress_percentage}% complete
              </p>
            </div>
          )}
          {taskStatus.status === 'FAILED' && taskStatus.error_message && (
            <p style={{ margin: '10px 0 0 0', color: '#F44336' }}>
              Error: {taskStatus.error_message}
            </p>
          )}
          {taskStatus.result && taskStatus.result.ado_push && (
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#4CAF50' }}>
              ✓ {taskStatus.result.ado_push.files_pushed} files pushed to branch: {taskStatus.result.ado_push.branch}
            </p>
          )}
          {isPolling && taskStatus.status === 'IN_PROGRESS' && taskStatus.progress_percentage < 100 && (
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#999' }}>
              Checking status every 5 seconds...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversionTaskMonitor;
